import { useEffect, useRef, useState, type FormEvent } from 'react';
import {
  Alert,
  Box,
  Button,
  Collapse,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  FormControlLabel,
  FormHelperText,
  FormLabel,
  InputLabel,
  MenuItem,
  Radio,
  RadioGroup,
  Select,
  Stack,
  Switch,
  TextField,
  Typography,
  useTheme,
} from '@mui/material';
import { visuallyHidden } from '@mui/utils';
import { useNavigate } from 'react-router-dom';
import { Trans, useTranslation } from 'react-i18next';
import {
  SUBTYPES_BY_TYPE,
  accountsApi,
  institutionLabel,
  type Account,
  type AccountCreateRequest,
  type AccountPatchRequest,
  type AccountSubtype,
  type AccountType,
  type LoanTerms,
  type PermissionFlags,
} from '@/api/accounts';
import { compactIdentifier, servedIdentifierKind } from '@/features/accounts/identifierDisplay';
import { sharesApi } from '@/api/shares';
import { useMe } from '@/session/meHooks';
import { useNotify } from '@/shell/NotifyProvider';
import { initialsOf } from '@/shell/initials';
import { personName } from '@/i18n/personName';
import { resolveTokens } from '@/theme/theme';
import { DialogSubtitle } from '@/components/DialogSubtitle';
import { useDialogFullScreen } from '@/shell/useDialogFullScreen';
import { ErrorCodes, isApiError, openingAfterFirstLine } from '@/api/errors';
import { applyBackendFields, validationAlert } from '@/features/shared/useFieldErrors';
import { SubscribeInPlace } from '@/features/subscribe/SubscribeInPlace';
import { SUBSCRIBE_CURRENCIES } from '@/features/subscribe/currencies';
import {
  bpToInputText,
  formatCivilDate,
  formatList,
  minorToInputText,
  parseMoneyToMinor,
  percentToBp,
} from '@/i18n/format';
import { useLocale } from '@/i18n/useLocale';
import { todayCivilInZone } from '@/i18n/zonedCivil';
import { allocationErrorKey } from './allocationErrors';
import { errorCopy } from '@/api/errorCopy';
import { ConfirmPanel } from '@/components/ConfirmDialog';
import { AutoApplyThresholdField } from '@/features/automation/AutoApplyThresholdField';
import { SubmitButton } from '@/components/SubmitButton';
import { GuardedDialog } from '@/components/GuardedDialog';
import { ErrorSummary, type ErrorSummaryItem } from '@/components/ErrorSummary';
import { useDialogDirtyGuard } from '@/shared/useDialogDirtyGuard';
import { useDirty } from '@/shared/useDirty';
import { InstitutionPicker, type InstitutionValue } from './InstitutionPicker';

const TITLE_ID = 'account-form-title';
const SUBTITLE_ID = 'account-form-subtitle';
const NAME_MAX_LENGTH = 120;
const NO_FLAGS: PermissionFlags = { canImport: false, canViewFull: false, canEdit: false };

/** A person the owner can grant this account to: an ACTIVE subscriber sharer (MM-D-33). */
interface GrantCandidate {
  userId: number;
  name: string;
}

interface AccountFormDialogProps {
  /** The owner's operating currency — offered first in the currency select. */
  operatingCurrency: string | null;
  /** Present when editing; absent when creating. */
  account?: Account;
  /** The category (type) a create is for; it picks the title, sub-line and subtype cards. */
  initialType?: AccountType;
  onClose: () => void;
  onSaved: (account: Account) => void | Promise<void>;
  /**
   * Create, then import (TMMAPP-0123 item 4): the created account's "Import now". Absent, the ready
   * body offers Done only.
   */
  onImport?: (account: Account) => void;
  /**
   * Edit opened from "Set / Edit starting balance" (TMMAPP-0123 item 16): the Advanced section opens
   * and the opening balance field takes focus.
   */
  focusOpening?: boolean;
  /**
   * Prefills the opening amount (the balance check's difference applied to the current opening).
   * The person still saves through this form.
   */
  suggestedOpeningMinor?: number;
  /** An IBAN, CLABE or account number to start a create with (TMMAPP-0133). */
  initialIdentifier?: string;
  /** Institution to start a create with. */
  initialInstitution?: string;
}

/**
 * The starting balance on create (TMMAPP-0123 item 15): from the first import (the default; neither
 * field is sent and the account's opening is pending) or entered now (both fields, required).
 */
type OpeningMode = 'IMPORT' | 'NOW';

interface FormState {
  type: AccountType;
  subtype: AccountSubtype;
  name: string;
  institution: InstitutionValue;
  last4: string;
  identifier: string;
  currency: string;
  openingMode: OpeningMode;
  opening: string;
  openingOn: string;
  apr: string;
  loanOriginal: string;
  originatedOn: string;
  payment: string;
  paymentDay: string;
  maturesOn: string;
  management: 'MANAGED' | 'UNMANAGED';
  includeInNetWorth: boolean;
}

type FieldKey =
  | 'name'
  | 'last4'
  | 'identifier'
  | 'opening'
  | 'openingOn'
  | 'apr'
  | 'loanOriginal'
  | 'originatedOn'
  | 'payment'
  | 'paymentDay'
  | 'maturesOn';
type FieldErrors = Partial<Record<FieldKey, true>>;

/** The summary's order and labels: the form's visual order, each field's own label key. */
const SUMMARY_FIELDS: readonly { key: FieldKey; label: string; editLabel?: string }[] = [
  { key: 'name', label: 'accounts.nameLabel', editLabel: 'accounts.editNameLabel' },
  { key: 'last4', label: 'accounts.last4Label', editLabel: 'accounts.editLast4Label' },
  { key: 'identifier', label: 'accounts.identifierLabel' },
  { key: 'opening', label: 'accounts.openingBalanceLabel' },
  { key: 'openingOn', label: 'accounts.openingOnLabel' },
  { key: 'apr', label: 'accounts.aprLabel' },
  { key: 'loanOriginal', label: 'accounts.loanOriginalLabel' },
  { key: 'originatedOn', label: 'accounts.loanOriginatedOnLabel' },
  { key: 'payment', label: 'accounts.loanPaymentLabel' },
  { key: 'paymentDay', label: 'accounts.loanPaymentDayLabel' },
  { key: 'maturesOn', label: 'accounts.loanMaturesOnLabel' },
];
const fieldId = (key: FieldKey) => `account-form-${key}`;
/** The fields an edit keeps under "Advanced"; an error in one opens the disclosure. */
const ADVANCED_IDS: ReadonlySet<string> = new Set(
  SUMMARY_FIELDS.filter(({ key }) => key !== 'name' && key !== 'last4').map(({ key }) =>
    fieldId(key),
  ),
);

function initialState(
  account: Account | undefined,
  operating: string | null,
  locale: string,
  initialType: AccountType = 'CASH',
  suggestedOpeningMinor?: number,
  initialIdentifier = '',
  initialInstitution = '',
): FormState {
  // No served currency: the select starts empty and the person chooses one (never a guess).
  const currency = account?.currency ?? operating ?? '';
  const type = account?.type ?? initialType;
  return {
    type,
    subtype: account?.subtype ?? SUBTYPES_BY_TYPE[type][0]!,
    name: account?.name ?? '',
    // A set catalog institution shows by its catalog name (TMMAPP-0165 item 17).
    institution: {
      id: account?.institutionId ?? null,
      name: account === undefined ? initialInstitution : (institutionLabel(account) ?? ''),
    },
    last4: account?.last4 ?? '',
    identifier: account?.externalIdentifier ?? initialIdentifier,
    currency,
    openingMode: 'IMPORT',
    opening:
      suggestedOpeningMinor != null
        ? minorToInputText(suggestedOpeningMinor, currency, locale)
        : account?.openingBalanceMinor != null
          ? minorToInputText(account.openingBalanceMinor, currency, locale)
          : '',
    // Create starts blank and requires a choice (TMMAPP-0120 item 11); edit keeps the stored date.
    openingOn: account?.openingBalanceOn ?? '',
    apr: account?.aprBp != null ? bpToInputText(account.aprBp, locale) : '',
    loanOriginal:
      account?.loan != null ? minorToInputText(account.loan.originalMinor, currency, locale) : '',
    originatedOn: account?.loan?.originatedOn ?? '',
    payment:
      account?.loan != null ? minorToInputText(account.loan.paymentMinor, currency, locale) : '',
    paymentDay: account?.loan != null ? String(account.loan.paymentDay) : '',
    maturesOn: account?.loan?.maturesOn ?? '',
    management: account?.management ?? 'MANAGED',
    includeInNetWorth: account?.includeInNetWorth ?? false,
  };
}

function currencyOptions(operating: string | null): string[] {
  const rest = SUBSCRIBE_CURRENCIES.filter((code) => code !== operating);
  return operating === null ? rest : [operating, ...rest];
}

const OPENING_SERVER_FIELDS = ['openingBalanceMinor', 'openingBalanceOn'] as const;
type OpeningServerField = (typeof OPENING_SERVER_FIELDS)[number];

/**
 * Create / edit an account (design §8.6). Type and subtype are immutable on edit. Currency is
 * sent only when it changes (`ACCOUNT_CURRENCY_IMMUTABLE` when transactions exist). The opening
 * balance and date stay editable after transactions exist and are sent when they change.
 */
export function AccountFormDialog({
  operatingCurrency,
  account,
  initialType = 'CASH',
  onClose,
  onSaved,
  onImport,
  focusOpening = false,
  suggestedOpeningMinor,
  initialIdentifier = '',
  initialInstitution = '',
}: AccountFormDialogProps) {
  const { t } = useTranslation();
  const locale = useLocale();
  const me = useMe();
  const notify = useNotify();
  const navigate = useNavigate();
  const tokens = resolveTokens(useTheme());
  const fullScreen = useDialogFullScreen();
  const [advancedOpen, setAdvancedOpen] = useState(focusOpening);
  const [candidates, setCandidates] = useState<GrantCandidate[]>([]);
  const [grants, setGrants] = useState<Record<number, PermissionFlags>>({});
  const [aiMatchAutoApplyBp, setAiMatchAutoApplyBp] = useState(account?.aiMatchAutoApplyBp ?? null);
  const [form, setForm] = useState<FormState>(() =>
    initialState(
      account,
      operatingCurrency,
      locale,
      initialType,
      suggestedOpeningMinor,
      initialIdentifier,
      initialInstitution,
    ),
  );
  const [submitting, setSubmitting] = useState(false);
  const [fieldErrors, setFieldErrors] = useState<FieldErrors>({});
  const [serverOpening, setServerOpening] = useState<Partial<Record<OpeningServerField, string>>>(
    {},
  );
  const [serverIdentifier, setServerIdentifier] = useState<string | undefined>();
  const [submitMessage, setSubmitMessage] = useState<string | null>(null);
  const [subscribeRequired, setSubscribeRequired] = useState(false);
  // The account a create made: the body swaps in place to "{name} is ready" (DLG-005, item 4).
  const [created, setCreated] = useState<Account | null>(null);
  // Edit: a managed / unmanaged switch confirms first (TMMAPP-0139 item 7); a refusal stays here.
  const [switchAsk, setSwitchAsk] = useState<{ error: string | null } | null>(null);
  const readyTitleRef = useRef<HTMLHeadingElement>(null);
  const nameRef = useRef<HTMLInputElement>(null);
  /** The last check's errors, read on submit before the state update lands. */
  const lastErrors = useRef<FieldErrors>({});
  const editing = account !== undefined;
  const trimmedName = form.name.trim();
  // The opening date can't be after today in the person's zone (TMMAPI-0160 U2). Like tmm-api, it is
  // checked only when the date is sent: on create, on a pending account, or when an edit changes it,
  // so a stored date from before the rule never blocks an unrelated edit.
  const today = todayCivilInZone(me.user.timezone ?? 'UTC');
  const openingDateSent =
    account === undefined || account.openingPending || form.openingOn !== account.openingBalanceOn;
  const openingFuture =
    openingDateSent && /^\d{4}-\d{2}-\d{2}$/.test(form.openingOn) && form.openingOn > today;
  // Create: an unmanaged account keeps both fields; a managed one asks first (item 15). Edit always
  // shows them; a pending account's may stay blank (both or neither).
  const openingChoice = !editing && form.management === 'MANAGED';
  const openingShown = !openingChoice || form.openingMode === 'NOW';
  const openingOptional = editing && account.openingPending;
  const { dirty } = useDirty({ form, aiMatchAutoApplyBp, grants });
  const guard = useDialogDirtyGuard(dirty && created === null, onClose, submitting);

  useEffect(() => {
    if (created !== null) readyTitleRef.current?.focus();
  }, [created]);

  const openingRef = useRef<HTMLInputElement>(null);

  // Every field in error, in visual order, for the summary a failed submit focuses.
  const summaryItems: ErrorSummaryItem[] = SUMMARY_FIELDS.flatMap(({ key, label, editLabel }) => {
    const server =
      key === 'opening'
        ? serverOpening.openingBalanceMinor
        : key === 'openingOn'
          ? serverOpening.openingBalanceOn
          : key === 'identifier'
            ? serverIdentifier
            : undefined;
    if (fieldErrors[key] !== true && server === undefined) return [];
    return [
      {
        id: fieldId(key),
        label: t(editing && editLabel !== undefined ? editLabel : label),
        message:
          server ??
          (key === 'openingOn' && openingFuture
            ? t('errors.server.notFuture')
            : t('errors.fieldInvalid')),
      },
    ];
  });
  const advancedShown =
    advancedOpen ||
    (editing &&
      (serverIdentifier !== undefined || summaryItems.some((item) => ADVANCED_IDS.has(item.id))));

  // Grant candidates (create only): the distinct ACTIVE sharers across the owner's own virtual
  // accounts, subscribers only — a guest seat is never offered (MM-D-33). The shares lists are the
  // only people directory the SPA has.
  useEffect(() => {
    if (editing) return;
    let cancelled = false;
    const mine = me.virtualAccounts.filter((virtualAccount) => virtualAccount.mine);
    void Promise.all(mine.map((virtualAccount) => sharesApi.list(virtualAccount.id)))
      .then((lists) => {
        if (cancelled) return;
        const seen = new Map<number, GrantCandidate>();
        for (const share of lists.flat()) {
          if (share.status !== 'ACTIVE' || share.userId === null) continue;
          if (share.seat !== 'SUBSCRIBER' || seen.has(share.userId)) continue;
          seen.set(share.userId, {
            userId: share.userId,
            name: personName(share.displayName, t),
          });
        }
        setCandidates([...seen.values()]);
      })
      .catch(() => {
        if (!cancelled) setCandidates([]);
      });
    return () => {
      cancelled = true;
    };
  }, [editing, me.virtualAccounts, t]);

  function toggleGrant(userId: number, flag: keyof PermissionFlags) {
    setGrants((prev) => {
      const current = prev[userId] ?? NO_FLAGS;
      const next = { ...current, [flag]: !current[flag] };
      // can_edit implies can_view_full (and so cannot stay on without it).
      if (flag === 'canEdit' && next.canEdit) next.canViewFull = true;
      if (flag === 'canViewFull' && !next.canViewFull) next.canEdit = false;
      return { ...prev, [userId]: next };
    });
  }

  const set = <K extends keyof FormState>(key: K, value: FormState[K]) =>
    setForm((prev) => ({ ...prev, [key]: value }));

  function collect(): AccountCreateRequest | null {
    const errors: FieldErrors = {};
    if (trimmedName.length === 0) errors.name = true;
    if (form.last4.length > 0 && !/^\d{4}$/.test(form.last4)) errors.last4 = true;
    // Both or neither on a pending account; both on any other shown form; none when deferred.
    const openingBlank = form.opening.trim().length === 0 && form.openingOn === '';
    const openingSent = openingShown && !(openingOptional && openingBlank);
    const opening = openingSent ? parseMoneyToMinor(form.opening, form.currency, locale) : null;
    if (openingSent && opening === null) errors.opening = true;
    if (openingSent && (!/^\d{4}-\d{2}-\d{2}$/.test(form.openingOn) || openingFuture)) {
      errors.openingOn = true;
    }
    let aprBp: number | undefined;
    if ((form.type === 'REVOLVING' || form.type === 'LOAN') && form.apr.trim().length > 0) {
      const parsed = percentToBp(form.apr, locale);
      if (parsed === null) errors.apr = true;
      else aprBp = parsed;
    }
    let loan: LoanTerms | undefined;
    if (form.type === 'LOAN') {
      const original = parseMoneyToMinor(form.loanOriginal, form.currency, locale);
      const payment = parseMoneyToMinor(form.payment, form.currency, locale);
      const day = Number(form.paymentDay);
      if (original === null) errors.loanOriginal = true;
      if (!/^\d{4}-\d{2}-\d{2}$/.test(form.originatedOn)) errors.originatedOn = true;
      if (payment === null) errors.payment = true;
      if (!Number.isInteger(day) || day < 1 || day > 31) errors.paymentDay = true;
      if (!/^\d{4}-\d{2}-\d{2}$/.test(form.maturesOn)) errors.maturesOn = true;
      if (
        original !== null &&
        payment !== null &&
        Number.isInteger(day) &&
        day >= 1 &&
        day <= 31 &&
        /^\d{4}-\d{2}-\d{2}$/.test(form.originatedOn) &&
        /^\d{4}-\d{2}-\d{2}$/.test(form.maturesOn)
      ) {
        loan = {
          originalMinor: original,
          originatedOn: form.originatedOn,
          paymentMinor: payment,
          paymentDay: day,
          maturesOn: form.maturesOn,
        };
      }
    }
    lastErrors.current = errors;
    setFieldErrors(errors);
    if (Object.keys(errors).length > 0) return null;
    const body: AccountCreateRequest = {
      type: form.type,
      subtype: form.subtype,
      name: trimmedName,
    };
    if (openingSent && opening !== null) {
      body.openingBalanceMinor = opening;
      body.openingBalanceOn = form.openingOn;
    }
    // A pick sends its id alone; the free text is sent only when typed (TMMAPP-0165 item 17).
    const institution = form.institution.name.trim();
    if (form.institution.id !== null) body.institutionId = form.institution.id;
    else if (institution.length > 0) body.institution = institution;
    if (form.last4.length === 4) body.last4 = form.last4;
    const identifier = compactIdentifier(form.identifier);
    if (identifier.length > 0) body.externalIdentifier = identifier;
    if (form.currency !== '' && form.currency !== operatingCurrency) body.currency = form.currency;
    if (aprBp !== undefined) body.aprBp = aprBp;
    if (loan !== undefined) body.loan = loan;
    if (!editing) body.management = form.management;
    // The net-worth switch is an unmanaged account's: a managed create sends false, as the
    // own-account flow's form does (TMMAPP-0139 item 7).
    body.includeInNetWorth =
      !editing && form.management === 'MANAGED' ? false : form.includeInNetWorth;
    return body;
  }

  function diff(current: Account, next: AccountCreateRequest): AccountPatchRequest {
    const body: AccountPatchRequest = {};
    if (
      current.access === 'OWNER' &&
      current.management === 'MANAGED' &&
      aiMatchAutoApplyBp !== (current.aiMatchAutoApplyBp ?? null)
    )
      body.aiMatchAutoApplyBp = aiMatchAutoApplyBp;
    if (next.name !== current.name) body.name = next.name;
    // The owner's free text changes only when they typed one; a pick leaves it as it is.
    const institution = next.institution ?? '';
    if (next.institutionId === undefined && institution !== (current.institution ?? '')) {
      body.institution = institution.length > 0 ? institution : undefined;
    }
    // The catalog institution (TMMAPP-0163 item 11): a pick sends its id; a typed name or an emptied
    // field clears a set one (`null`). Unchanged, it is left out.
    const institutionId = next.institutionId ?? null;
    if (institutionId !== (current.institutionId ?? null)) body.institutionId = institutionId;
    // An emptied field clears both in one patch (TMMAPP-0166 item 5): `institution: ""` clears the
    // typed name (`null` or leaving it out keeps it), so it never comes back as the label.
    const hadInstitution =
      (current.institution ?? '').length > 0 || (current.institutionId ?? null) !== null;
    if (next.institutionId === undefined && institution.length === 0 && hadInstitution) {
      body.institution = '';
      body.institutionId = null;
    }
    const last4 = next.last4 ?? '';
    if (last4 !== (current.last4 ?? '')) body.last4 = last4.length > 0 ? last4 : undefined;
    const identifier = compactIdentifier(form.identifier);
    if (identifier !== compactIdentifier(current.externalIdentifier ?? '')) {
      body.externalIdentifier = identifier;
    }
    if (next.currency !== undefined && next.currency !== (current.currency ?? operatingCurrency)) {
      body.currency = next.currency;
    }
    if (
      current.currency !== null &&
      form.currency !== current.currency &&
      next.currency === undefined
    ) {
      // Switching back to the operating currency still has to be sent.
      body.currency = form.currency;
    }
    if (
      next.openingBalanceMinor !== undefined &&
      next.openingBalanceMinor !== current.openingBalanceMinor
    ) {
      body.openingBalanceMinor = next.openingBalanceMinor;
    }
    if (next.openingBalanceOn !== undefined && next.openingBalanceOn !== current.openingBalanceOn) {
      body.openingBalanceOn = next.openingBalanceOn;
    }
    // A pending opening is set whole (TMMAPI-0160 U2): both fields go together.
    if (
      current.openingPending &&
      next.openingBalanceMinor !== undefined &&
      next.openingBalanceOn !== undefined
    ) {
      body.openingBalanceMinor = next.openingBalanceMinor;
      body.openingBalanceOn = next.openingBalanceOn;
    }
    if (next.aprBp !== undefined && next.aprBp !== current.aprBp) body.aprBp = next.aprBp;
    if (next.loan !== undefined) body.loan = next.loan;
    if (
      next.includeInNetWorth !== undefined &&
      next.includeInNetWorth !== current.includeInNetWorth
    ) {
      body.includeInNetWorth = next.includeInNetWorth;
    }
    return body;
  }

  // An owner's edit that changes how the account is tracked (`AccountPatchRequest.management`).
  const switching =
    account !== undefined && account.access === 'OWNER' && form.management !== account.management;

  function handleSubmit(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    void submit(false);
  }

  async function submit(switchConfirmed: boolean) {
    if (submitting) return;
    const collected = collect();
    if (collected === null) {
      // A blank opening date on create: focus goes to the date itself, under its own message,
      // unless a field above it needs attention first.
      const first = SUMMARY_FIELDS.find(({ key }) => lastErrors.current[key] === true)?.key;
      if (!editing && form.openingOn === '' && first === 'openingOn') {
        document.getElementById(fieldId('openingOn'))?.focus();
        return;
      }
      guard.focusErrors();
      return;
    }
    if (switching && !switchConfirmed) {
      setSwitchAsk({ error: null });
      return;
    }
    setSubmitting(true);
    setSubmitMessage(null);
    setServerOpening({});
    setServerIdentifier(undefined);
    try {
      const saved = account
        ? await accountsApi.update(account.id, {
            ...diff(account, collected),
            ...(switching ? { management: form.management } : {}),
          })
        : await accountsApi.create(collected);
      // Create-then-grant: the account exists now, so a failed grant never undoes it. The failure
      // is reported and the owner lands on the Permissions tab to finish it.
      const failures: { name: string; reason: string }[] = [];
      if (!account && form.management === 'MANAGED') {
        for (const person of candidates) {
          const flags = grants[person.userId];
          if (flags === undefined || !(flags.canImport || flags.canViewFull || flags.canEdit)) {
            continue;
          }
          try {
            await accountsApi.putPermission(saved.id, person.userId, flags);
          } catch (grantError) {
            failures.push({
              name: person.name,
              reason: t(allocationErrorKey(grantError) ?? 'accounts.errorGeneric'),
            });
          }
        }
      }
      await onSaved(saved);
      if (failures.length > 0) {
        notify(
          t('accounts.grantFailed', {
            names: formatList(
              failures.map((failure) => failure.name),
              locale,
            ),
            reason: [...new Set(failures.map((failure) => failure.reason))].join('; '),
          }),
          { severity: 'warning' },
        );
        void navigate(`/me/accounts/${saved.id}?tab=permissions`);
        onClose();
        return;
      }
      if (account) {
        // Now managed: offer IMPORT as the add flow does (the ready body), when the host can.
        if (switching && saved.management === 'MANAGED' && onImport !== undefined) {
          setSwitchAsk(null);
          setSubmitting(false);
          setCreated(saved);
          return;
        }
        onClose();
        return;
      }
      setSubmitting(false);
      setCreated(saved);
    } catch (error) {
      const afterFirstLine = openingAfterFirstLine(error);
      if (switchConfirmed) {
        // A refused switch says its code's sentence in the confirmation (no code is documented).
        setSwitchAsk({ error: errorCopy(t, error, 'accounts.errorGeneric') });
      } else if (isApiError(error) && error.code === ErrorCodes.SUBSCRIPTION_REQUIRED) {
        setSubscribeRequired(true);
      } else if (afterFirstLine !== null) {
        // The opening must be dated before the account's earliest line: said at the date field.
        const message =
          afterFirstLine.earliestPostedOn !== null
            ? t('accounts.openingAfterFirstLine', {
                date: formatCivilDate(afterFirstLine.earliestPostedOn, locale),
              })
            : t('errors.fieldInvalid');
        setServerOpening({ openingBalanceOn: message });
        setSubmitMessage(t('accounts.errorValidation'));
        guard.focusErrors();
      } else if (isApiError(error) && error.code === ErrorCodes.VALIDATION_FAILED) {
        const next: Partial<Record<OpeningServerField, string>> = {};
        let identifierMessage: string | undefined;
        const result = applyBackendFields(
          error,
          [...OPENING_SERVER_FIELDS, 'externalIdentifier'] as const,
          (field, message) => {
            if (field === 'externalIdentifier') identifierMessage = message;
            else next[field] = message;
          },
          t,
          guard.focusErrors,
        );
        setServerOpening(next);
        setServerIdentifier(identifierMessage);
        setSubmitMessage(validationAlert(result, error, t, t('accounts.errorValidation')));
      } else {
        setSubmitMessage(t(allocationErrorKey(error) ?? 'accounts.errorGeneric'));
      }
      setSubmitting(false);
    }
  }

  const currencyField = (
    <FormControl fullWidth>
      <InputLabel id="account-form-currency">{t('accounts.currencyLabel')}</InputLabel>
      <Select
        labelId="account-form-currency"
        label={t('accounts.currencyLabel')}
        value={form.currency}
        onChange={(event) => set('currency', event.target.value)}
      >
        {currencyOptions(operatingCurrency).map((code) => (
          <MenuItem key={code} value={code}>
            {code}
          </MenuItem>
        ))}
      </Select>
      {operatingCurrency !== null && (
        <FormHelperText>
          {t('accounts.currencyHelp', { operating: operatingCurrency })}
        </FormHelperText>
      )}
    </FormControl>
  );

  const clearServer = (field: OpeningServerField) =>
    setServerOpening((prev) => {
      if (prev[field] === undefined) return prev;
      const next = { ...prev };
      delete next[field];
      return next;
    });

  // The currency, opening balance and date, APR and loan terms, and the net-worth switch: shown in
  // the Add grid, and under "Advanced" when editing (D1: every editable field stays).
  const detailFields = (
    <>
      {(form.management === 'UNMANAGED' || (editing && account?.management === 'UNMANAGED')) && (
        <FormControlLabel
          sx={{ gridColumn: '1 / -1', justifySelf: 'start' }}
          control={
            <Switch
              checked={form.includeInNetWorth}
              onChange={(_e, checked) => set('includeInNetWorth', checked)}
            />
          }
          label={t('accounts.includeInNetWorth')}
        />
      )}
      {currencyField}
      {openingChoice && (
        <FormControl sx={{ gridColumn: '1 / -1' }}>
          <FormLabel id="account-form-opening-mode">{t('accounts.openingChoice.label')}</FormLabel>
          <RadioGroup
            aria-labelledby="account-form-opening-mode"
            value={form.openingMode}
            onChange={(_e, value) => set('openingMode', value as OpeningMode)}
          >
            <FormControlLabel
              value="IMPORT"
              control={<Radio />}
              label={t('accounts.openingChoice.fromImport')}
            />
            <FormControlLabel
              value="NOW"
              control={<Radio />}
              label={t('accounts.openingChoice.now')}
            />
          </RadioGroup>
          {form.openingMode === 'IMPORT' && (
            <FormHelperText>{t('accounts.openingChoice.fromImportHelp')}</FormHelperText>
          )}
        </FormControl>
      )}
      {openingShown && (
        <>
          <TextField
            id={fieldId('opening')}
            label={t('accounts.openingBalanceLabel')}
            value={form.opening}
            onChange={(event) => {
              set('opening', event.target.value);
              clearServer('openingBalanceMinor');
            }}
            inputRef={openingRef}
            required={!openingOptional}
            fullWidth
            error={fieldErrors.opening === true || serverOpening.openingBalanceMinor !== undefined}
            helperText={
              serverOpening.openingBalanceMinor ??
              (openingOptional
                ? t('accounts.openingPendingHelp')
                : t(`accounts.openingBalanceHelp.${form.type}`))
            }
          />
          <TextField
            type="date"
            id={fieldId('openingOn')}
            label={t('accounts.openingOnLabel')}
            value={form.openingOn}
            onChange={(event) => {
              set('openingOn', event.target.value);
              clearServer('openingBalanceOn');
            }}
            required={!openingOptional}
            fullWidth
            error={fieldErrors.openingOn === true || serverOpening.openingBalanceOn !== undefined}
            helperText={
              serverOpening.openingBalanceOn ??
              (fieldErrors.openingOn === true && form.openingOn === ''
                ? t('accounts.openingOnRequired')
                : openingFuture
                  ? t('errors.server.notFuture')
                  : t(editing ? 'accounts.openingHelpEdit' : 'accounts.openingHelpCreate'))
            }
            slotProps={{ inputLabel: { shrink: true }, htmlInput: { max: today } }}
          />
        </>
      )}
      {(form.type === 'REVOLVING' || form.type === 'LOAN') && (
        <TextField
          id={fieldId('apr')}
          label={t('accounts.aprLabel')}
          value={form.apr}
          onChange={(event) => set('apr', event.target.value)}
          fullWidth
          error={fieldErrors.apr === true}
          helperText={t('accounts.aprHelp')}
        />
      )}
      {form.type === 'LOAN' && (
        <>
          <TextField
            id={fieldId('loanOriginal')}
            label={t('accounts.loanOriginalLabel')}
            value={form.loanOriginal}
            onChange={(event) => set('loanOriginal', event.target.value)}
            required
            fullWidth
            error={fieldErrors.loanOriginal === true}
            helperText={t('accounts.loanOriginalHelp')}
          />
          <TextField
            type="date"
            id={fieldId('originatedOn')}
            label={t('accounts.loanOriginatedOnLabel')}
            value={form.originatedOn}
            onChange={(event) => set('originatedOn', event.target.value)}
            required
            fullWidth
            error={fieldErrors.originatedOn === true}
            helperText={t('accounts.loanOriginatedOnHelp')}
            slotProps={{ inputLabel: { shrink: true } }}
          />
          <TextField
            id={fieldId('payment')}
            label={t('accounts.loanPaymentLabel')}
            value={form.payment}
            onChange={(event) => set('payment', event.target.value)}
            required
            fullWidth
            error={fieldErrors.payment === true}
            helperText={t('accounts.loanPaymentHelp')}
          />
          <TextField
            id={fieldId('paymentDay')}
            label={t('accounts.loanPaymentDayLabel')}
            value={form.paymentDay}
            onChange={(event) => set('paymentDay', event.target.value)}
            required
            fullWidth
            error={fieldErrors.paymentDay === true}
            helperText={t('accounts.loanPaymentDayHelp')}
            slotProps={{ htmlInput: { inputMode: 'numeric', min: 1, max: 31 } }}
          />
          <TextField
            type="date"
            id={fieldId('maturesOn')}
            label={t('accounts.loanMaturesOnLabel')}
            value={form.maturesOn}
            onChange={(event) => set('maturesOn', event.target.value)}
            required
            fullWidth
            error={fieldErrors.maturesOn === true}
            helperText={t('accounts.loanMaturesOnHelp')}
            slotProps={{ inputLabel: { shrink: true } }}
          />
        </>
      )}
    </>
  );

  const nameField = (
    <TextField
      id={fieldId('name')}
      label={t(editing ? 'accounts.editNameLabel' : 'accounts.nameLabel')}
      value={form.name}
      onChange={(event) => set('name', event.target.value)}
      inputRef={nameRef}
      required
      fullWidth
      error={fieldErrors.name === true}
      helperText={editing ? undefined : t('accounts.nameHelp')}
      placeholder={editing ? undefined : t(`accounts.namePlaceholder.${form.subtype}`)}
      slotProps={{ htmlInput: { maxLength: NAME_MAX_LENGTH } }}
      sx={{ gridColumn: '1 / -1' }}
    />
  );

  const identifierKind = servedIdentifierKind(account?.externalIdentifierKind);
  const identifierUnchanged =
    compactIdentifier(form.identifier) === compactIdentifier(account?.externalIdentifier ?? '');
  const identifierField = (
    <TextField
      id={fieldId('identifier')}
      label={t('accounts.identifierLabel')}
      value={form.identifier}
      onChange={(event) => {
        set('identifier', event.target.value);
        if (serverIdentifier !== undefined) setServerIdentifier(undefined);
      }}
      fullWidth
      error={serverIdentifier !== undefined}
      helperText={
        serverIdentifier ??
        (identifierKind !== null && identifierUnchanged
          ? t(`accounts.identifierKind.${identifierKind}`)
          : t('accounts.identifierHelp'))
      }
      slotProps={{ htmlInput: { maxLength: 42 } }}
      sx={{ gridColumn: '1 / -1' }}
    />
  );

  const identifyingFields =
    form.management === 'MANAGED' ? (
      <>
        <InstitutionPicker
          label={t(editing ? 'accounts.editInstitutionLabel' : 'accounts.institutionLabel')}
          value={form.institution}
          onChange={(next) => set('institution', next)}
          placeholder={editing ? undefined : t('accounts.institutionPlaceholder')}
        />
        <TextField
          id={fieldId('last4')}
          label={t(editing ? 'accounts.editLast4Label' : 'accounts.last4Label')}
          value={form.last4}
          onChange={(event) => set('last4', event.target.value.replace(/\D/g, '').slice(0, 4))}
          fullWidth
          error={fieldErrors.last4 === true}
          helperText={t('accounts.last4Help')}
          placeholder={editing ? undefined : t('accounts.last4Placeholder')}
          slotProps={{ htmlInput: { inputMode: 'numeric', maxLength: 4 } }}
        />
      </>
    ) : null;

  const autoApply =
    account?.access === 'OWNER' && account.management === 'MANAGED' ? (
      <Box sx={{ gridColumn: '1 / -1' }}>
        <AutoApplyThresholdField
          label={t('autoApply.matchLabel')}
          value={aiMatchAutoApplyBp}
          onChange={setAiMatchAutoApplyBp}
          automationKeys={['AI_MATCH_SUGGESTION', 'AUTO_PLAN_MATCH']}
          disabled={submitting}
        />
      </Box>
    ) : null;

  const gridSx = {
    display: 'grid',
    gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr' },
    gap: 1.5,
    alignItems: 'start',
  } as const;

  const managementField = (
    <FormControl sx={{ gridColumn: '1 / -1' }}>
      <FormLabel id="account-form-management">{t('accounts.trackHow')}</FormLabel>
      <RadioGroup
        aria-labelledby="account-form-management"
        value={form.management}
        onChange={(_e, value) => set('management', value as 'MANAGED' | 'UNMANAGED')}
      >
        <FormControlLabel value="MANAGED" control={<Radio />} label={t('accounts.managed')} />
        <FormControlLabel value="UNMANAGED" control={<Radio />} label={t('accounts.unmanaged')} />
      </RadioGroup>
      {form.management === 'UNMANAGED' && (
        <FormHelperText>{t('accounts.unmanagedHelp')}</FormHelperText>
      )}
    </FormControl>
  );

  const addBody = (
    <>
      <Box>
        <Typography
          id="account-form-type"
          variant="overline"
          component="div"
          sx={{ mb: 1, color: tokens.textLabel('surfaceRaised') }}
        >
          {t('accounts.typeLabel')}
        </Typography>
        <Box
          role="radiogroup"
          aria-labelledby="account-form-type"
          sx={{ display: 'flex', gap: 1.5, flexWrap: 'wrap' }}
        >
          {SUBTYPES_BY_TYPE[form.type].map((subtype) => (
            <SubtypeCard
              key={subtype}
              checked={form.subtype === subtype}
              label={t(`accounts.subtype.${subtype}`)}
              note={t(`accounts.subtypeNote.${subtype}`)}
              onSelect={() => set('subtype', subtype)}
            />
          ))}
        </Box>
      </Box>
      <Box sx={gridSx}>
        {managementField}
        {nameField}
        {identifyingFields}
        {identifierField}
        {detailFields}
      </Box>
      {form.management === 'MANAGED' && (
        <GrantPermissions
          candidates={candidates}
          grants={grants}
          onToggle={toggleGrant}
          disabled={submitting}
        />
      )}
    </>
  );

  const editBody = (
    <Stack spacing={1.5}>
      <Box sx={gridSx}>
        {nameField}
        {identifyingFields}
        {/* The owner switches how it is tracked (TMMAPP-0139 item 7); a grantee never does. */}
        {account?.access === 'OWNER' && managementField}
      </Box>
      <Box
        data-currency-note=""
        sx={{
          border: `1px solid ${tokens.line}`,
          borderRadius: `${tokens.radius.control}px`,
          p: '11px 14px',
          typography: 'caption',
          color: tokens.text2,
          lineHeight: 1.6,
        }}
      >
        {t('accounts.editCurrencyNote', {
          currency: account?.currency ?? operatingCurrency,
          operating: operatingCurrency,
        })}
      </Box>
      <Box>
        <Button
          variant="text"
          size="small"
          aria-expanded={advancedShown}
          aria-controls="account-form-advanced"
          onClick={() => setAdvancedOpen((open) => !open)}
        >
          {t('accounts.advanced')}
        </Button>
        <Collapse in={advancedShown} unmountOnExit>
          <Box id="account-form-advanced" sx={{ ...gridSx, pt: 1.5 }}>
            {identifierField}
            {autoApply}
            {detailFields}
          </Box>
        </Collapse>
      </Box>
    </Stack>
  );

  if (created !== null) {
    const importable = created.management !== 'UNMANAGED' && onImport !== undefined;
    return (
      <GuardedDialog
        guard={guard}
        titleId={TITLE_ID}
        fullWidth
        maxWidth={false}
        fullScreen={fullScreen}
        slotProps={{
          paper: { sx: { maxWidth: '100%', ...(fullScreen ? {} : { width: 520 }) } },
        }}
      >
        <DialogTitle id={TITLE_ID} ref={readyTitleRef} tabIndex={-1} data-account-ready="">
          {t('accounts.readyTitle', { name: created.name })}
        </DialogTitle>
        <DialogContent>
          <Typography>
            {importable ? t('accounts.readyImport') : t('accounts.readyUnmanaged')}
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={onClose}>{t('common.done')}</Button>
          {importable && (
            <Button variant="contained" onClick={() => onImport(created)}>
              {t('accounts.importNow')}
            </Button>
          )}
        </DialogActions>
      </GuardedDialog>
    );
  }

  if (switchAsk !== null && account !== undefined) {
    const toManaged = form.management === 'MANAGED';
    return (
      <GuardedDialog
        guard={guard}
        titleId={TITLE_ID}
        fullWidth
        maxWidth={false}
        fullScreen={fullScreen}
        slotProps={{
          paper: { sx: { maxWidth: '100%', ...(fullScreen ? {} : { width: 460 }) } },
        }}
      >
        <ConfirmPanel
          titleId={TITLE_ID}
          title={t(toManaged ? 'accounts.switchManagedTitle' : 'accounts.switchUnmanagedTitle', {
            name: account.name,
          })}
          consequence={t(toManaged ? 'accounts.switchManagedBody' : 'accounts.switchUnmanagedBody')}
          confirmLabel={t(
            toManaged ? 'accounts.switchManagedConfirm' : 'accounts.switchUnmanagedConfirm',
          )}
          busy={submitting}
          error={switchAsk.error}
          onConfirm={() => void submit(true)}
          onCancel={() => setSwitchAsk(null)}
        />
      </GuardedDialog>
    );
  }

  return (
    <GuardedDialog
      guard={guard}
      titleId={TITLE_ID}
      fullWidth
      maxWidth={false}
      fullScreen={fullScreen}
      subtitle={
        <DialogSubtitle id={SUBTITLE_ID}>
          {editing ? t('accounts.editSub') : t(`accounts.addSub.${form.type}`)}
        </DialogSubtitle>
      }
      subtitleId={SUBTITLE_ID}
      slotProps={{
        transition: {
          onEntered: () => (focusOpening ? openingRef : nameRef).current?.focus(),
        },
        paper: { sx: { maxWidth: '100%', ...(fullScreen ? {} : { width: editing ? 460 : 680 }) } },
      }}
    >
      <form onSubmit={handleSubmit} noValidate>
        <DialogTitle id={TITLE_ID}>
          {editing ? t('accounts.editTitle') : t(`accounts.addTitle.${form.type}`)}
        </DialogTitle>
        <DialogContent>
          {subscribeRequired ? (
            <SubscribeInPlace onClose={onClose} />
          ) : (
            <Stack spacing={2} sx={{ mt: 0.5 }}>
              {summaryItems.length > 0 ? (
                <ErrorSummary items={summaryItems} message={submitMessage} />
              ) : (
                submitMessage !== null && <Alert severity="error">{submitMessage}</Alert>
              )}
              {editing ? editBody : addBody}
            </Stack>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={guard.requestClose} disabled={submitting}>
            {t('common.cancel')}
          </Button>
          {!subscribeRequired && (
            <SubmitButton type="submit" loading={submitting}>
              {editing ? t('common.save') : t('accounts.create')}
            </SubmitButton>
          )}
        </DialogActions>
      </form>
    </GuardedDialog>
  );
}

/**
 * A subtype radio card (P:2036): a real radio, visually hidden, in a label that draws the card —
 * min-width 150, padding 14; selected is the `selected` fill with a `brandBoundary` border, and
 * the radio's focus ring shows on the card.
 */
function SubtypeCard({
  checked,
  label,
  note,
  onSelect,
}: {
  checked: boolean;
  label: string;
  note: string;
  onSelect: () => void;
}) {
  const tokens = resolveTokens(useTheme());
  return (
    <Box
      component="label"
      data-checked={checked}
      sx={{
        flex: 1,
        minWidth: 150,
        cursor: 'pointer',
        border: `1px solid ${checked ? tokens.brandBoundary : tokens.line}`,
        bgcolor: tokens.surfaceRaised,
        backgroundImage: checked
          ? `linear-gradient(${tokens.selected}, ${tokens.selected})`
          : 'none',
        borderRadius: `${tokens.radius.control}px`,
        p: '14px',
        color: tokens.text,
        '&:has(input:focus-visible)': {
          outline: `2px solid ${tokens.focusRing}`,
          outlineOffset: 2,
        },
      }}
    >
      <input
        type="radio"
        name="account-subtype"
        checked={checked}
        onChange={onSelect}
        style={visuallyHidden}
      />
      <Typography component="span" sx={{ display: 'block', fontSize: 14, fontWeight: 500 }}>
        {label}
      </Typography>
      <Typography component="span" sx={{ display: 'block', fontSize: 12, color: tokens.text2 }}>
        {note}
      </Typography>
    </Box>
  );
}

/** The people you share any virtual account with, subscribers only, each with three flag pills. */
function GrantPermissions({
  candidates,
  grants,
  onToggle,
  disabled,
}: {
  candidates: GrantCandidate[];
  grants: Record<number, PermissionFlags>;
  onToggle: (userId: number, flag: keyof PermissionFlags) => void;
  disabled: boolean;
}) {
  const { t } = useTranslation();
  const tokens = resolveTokens(useTheme());
  const flags: { key: keyof PermissionFlags; label: string }[] = [
    { key: 'canImport', label: t('accounts.permImport') },
    { key: 'canViewFull', label: t('accounts.permViewFull') },
    { key: 'canEdit', label: t('accounts.permEdit') },
  ];
  return (
    <Box sx={{ borderTop: `1px solid ${tokens.line}`, pt: 1.75 }} data-grant-permissions="">
      <Typography
        id="account-form-grants"
        variant="overline"
        component="div"
        sx={{ mb: 1, color: tokens.textLabel('surfaceRaised') }}
      >
        {t('accounts.grantTitle')}
      </Typography>
      {candidates.length === 0 ? (
        <Typography variant="body2" sx={{ color: tokens.text2 }}>
          {t('accounts.grantNone')}
        </Typography>
      ) : (
        <Box
          component="ul"
          aria-labelledby="account-form-grants"
          sx={{ listStyle: 'none', m: 0, p: 0 }}
        >
          {candidates.map((person) => {
            const current = grants[person.userId] ?? NO_FLAGS;
            return (
              <Box
                component="li"
                key={person.userId}
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: 1.5,
                  py: 1.25,
                  borderBottom: `1px solid ${tokens.line}`,
                  flexWrap: 'wrap',
                }}
              >
                <Box
                  aria-hidden
                  sx={{
                    width: 28,
                    height: 28,
                    borderRadius: '50%',
                    bgcolor: tokens.surfaceRaised,
                    backgroundImage: `linear-gradient(${tokens.selected}, ${tokens.selected})`,
                    color: tokens.link,
                    display: 'grid',
                    placeItems: 'center',
                    fontSize: 12,
                    fontWeight: 500,
                  }}
                >
                  {initialsOf(person.name)}
                </Box>
                <Typography component="span" sx={{ flex: 1, minWidth: 120, fontSize: 14 }}>
                  {person.name}
                </Typography>
                <Box
                  role="group"
                  aria-label={t('accounts.grantPerson', { name: person.name })}
                  sx={{ display: 'flex', gap: 0.75, flexWrap: 'wrap' }}
                >
                  {flags.map(({ key, label }) => {
                    const on = current[key];
                    return (
                      <Button
                        key={key}
                        variant="text"
                        size="small"
                        aria-pressed={on}
                        disabled={disabled}
                        onClick={() => onToggle(person.userId, key)}
                        sx={{
                          border: `1px solid ${on ? tokens.brandBoundary : tokens.line}`,
                          borderRadius: '11px',
                          px: 1.5,
                          py: '4px',
                          minHeight: 0,
                          fontSize: 12,
                          fontWeight: 500,
                          textTransform: 'none',
                          letterSpacing: 0,
                          whiteSpace: 'nowrap',
                          bgcolor: tokens.surfaceRaised,
                          backgroundImage: on
                            ? `linear-gradient(${tokens.selected}, ${tokens.selected})`
                            : 'none',
                          color: on ? tokens.link : tokens.text2,
                        }}
                      >
                        {label}
                      </Button>
                    );
                  })}
                </Box>
              </Box>
            );
          })}
        </Box>
      )}
      <Typography
        variant="caption"
        component="p"
        sx={{ mt: 1.25, lineHeight: 1.6, color: tokens.textLabel('surfaceRaised') }}
      >
        <Trans
          i18nKey="accounts.grantNote"
          components={{ b: <Box component="strong" sx={{ fontWeight: 500 }} /> }}
        />
      </Typography>
    </Box>
  );
}
